import React, { useState, useEffect, useCallback } from 'react'
import { useAuthContext } from '../contexts/AuthContext'
import * as memoryService from '../services/memoryService'
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiArchive,
  FiLoader,
  FiX,
  FiSave,
  FiChevronLeft,
  FiChevronRight,
  FiTag,
} from 'react-icons/fi'
import { FaBrain } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import './MemoryManagementPage.css'
import { Button } from '@mui/material'
import { toast } from 'react-toastify'

const MAX_CONTENT_LENGTH = 2000
const ITEMS_PER_PAGE = 20

const MemoryManagementPage = () => {
  const { token } = useAuthContext()
  const navigate = useNavigate()

  // State
  const [memories, setMemories] = useState([])
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('active')
  const [isLoading, setIsLoading] = useState(false)

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create') // 'create' or 'edit'
  const [editingMemory, setEditingMemory] = useState(null)

  // Form state
  const [formData, setFormData] = useState({
    content: '',
    category: 'preference',
    importance: 'medium',
    tags: [],
  })
  const [tagInput, setTagInput] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // Fetch memories
  const fetchMemories = useCallback(async () => {
    if (!token) return

    setIsLoading(true)

    try {
      const skip = (currentPage - 1) * ITEMS_PER_PAGE
      const response = await memoryService.getMemories(token, {
        limit: ITEMS_PER_PAGE,
        skip,
        status_filter: statusFilter,
      })
      setMemories(response.items || [])
      setTotal(response.total || 0)
    } catch (err) {
      console.error('Error fetching memories:', err)
      toast.error(err.response?.data?.detail || 'Impossibile caricare le memorie')
    } finally {
      setIsLoading(false)
    }
  }, [token, currentPage, statusFilter])

  useEffect(() => {
    fetchMemories()
  }, [fetchMemories])

  // Handlers
  const handleOpenCreateModal = () => {
    setModalMode('create')
    setEditingMemory(null)
    setFormData({
      content: '',
      category: 'preference',
      importance: 'medium',
      tags: [],
    })
    setIsModalOpen(true)
  }

  const handleOpenEditModal = memory => {
    setModalMode('edit')
    setEditingMemory(memory)
    setFormData({
      content: memory.content,
      category: memory.category,
      importance: memory.importance,
      tags: memory.tags || [],
    })
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingMemory(null)
    setFormData({
      content: '',
      category: 'preference',
      importance: 'medium',
      tags: [],
    })
    setTagInput('')
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }))
      setTagInput('')
    }
  }

  const handleRemoveTag = tagToRemove => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }))
  }

  const handleSaveMemory = async () => {
    if (!formData.content.trim()) {
      toast.error('Il contenuto è obbligatorio')
      return
    }

    setIsSaving(true)

    try {
      if (modalMode === 'create') {
        await memoryService.createMemory(token, formData)
      } else {
        await memoryService.updateMemory(token, editingMemory.id, formData)
      }
      await fetchMemories()
      handleCloseModal()
    } catch (err) {
      console.error('Error saving memory:', err)
      toast.error(err.response?.data?.detail || 'Impossibile salvare la memoria')
    } finally {
      setIsSaving(false)
    }
  }

  const handleArchiveMemory = async memoryId => {
    if (
      !window.confirm(
        'Sei sicuro di voler archiviare questa memoria? Verrà rimossa dalla memoria attiva di F.R.A.N.C.A.'
      )
    )
      return

    try {
      await memoryService.archiveMemory(token, memoryId)
      await fetchMemories()
    } catch (err) {
      console.error('Error archiving memory:', err)
      toast.error(err.response?.data?.detail || 'Impossibile archiviare la memoria')
    }
  }

  const handleDeleteMemory = async memoryId => {
    if (
      !window.confirm(
        'Sei sicuro di voler eliminare definitivamente questa memoria? Questa azione non può essere annullata.'
      )
    )
      return

    try {
      await memoryService.deleteMemory(token, memoryId)
      await fetchMemories()
    } catch (err) {
      console.error('Error deleting memory:', err)
      toast.error(err.response?.data?.detail || 'Impossibile eliminare la memoria')
    }
  }

  // Pagination
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE)

  const getCategoryColor = category => {
    const colors = {
      preference: '#8b5cf6',
      instruction: '#3b82f6',
      fact: '#10b981',
      summary: '#f59e0b',
    }
    return colors[category] || '#6b7280'
  }

  const getImportanceColor = importance => {
    const colors = {
      low: '#9ca3af',
      medium: '#3b82f6',
      high: '#ef4444',
    }
    return colors[importance] || '#6b7280'
  }

  return (
    <div className="memory-page">
      {/* Header */}
      <div className="memory-header">
        <div className="header-top">
          <button className="back-button-mem" onClick={() => navigate(-1)}>
            <FiChevronLeft />
          </button>
          <div className="header-title">
            <h1>
              <FaBrain /> Le Mie Memorie
            </h1>
            <p className="header-subtitle">
              F.R.A.N.C.A. usa queste memorie per personalizzare automaticamente le tue
              conversazioni
            </p>
          </div>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{
              textTransform: 'none',
              backgroundColor: 'primary.main',
              px: 4,
              borderRadius: 2,
              gap: '8px',
            }}
            onClick={handleOpenCreateModal}
          >
            <FiPlus /> Nuova Memoria
          </Button>
        </div>

        {/* Filters */}
        <div className="memory-filters">
          <div className="filter-group">
            <label>Stato:</label>
            <select
              value={statusFilter}
              onChange={e => {
                setStatusFilter(e.target.value)
                setCurrentPage(1)
              }}
            >
              {memoryService.MEMORY_STATUS.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <div className="memory-stats">
            <span>
              Totale: <strong>{total}</strong> memorie
            </span>
          </div>
        </div>
      </div>

      {/* Memories List */}
      <div className="memory-content">
        {isLoading ? (
          <div className="loading-state">
            <FiLoader className="spinner" /> Caricamento memorie...
          </div>
        ) : memories.length === 0 ? (
          <div className="empty-state">
            <FaBrain size={64} />
            <h2>Nessuna memoria trovata</h2>
            <p>
              {statusFilter === 'active'
                ? 'Crea la tua prima memoria per aiutare F.R.A.N.C.A. a conoscerti meglio'
                : 'Non hai memorie archiviate'}
            </p>
            {statusFilter === 'active' && (
              <button className="create-button" onClick={handleOpenCreateModal}>
                <FiPlus /> Crea Prima Memoria
              </button>
            )}
          </div>
        ) : (
          <div className="memories-grid">
            {memories.map(memory => (
              <div key={memory.id} className="memory-card">
                <div className="memory-card-header">
                  <div className="memory-badges">
                    <span
                      className="badge category-badge"
                      style={{ backgroundColor: getCategoryColor(memory.category) }}
                    >
                      {
                        memoryService.MEMORY_CATEGORIES.find(
                          c => c.value === memory.category
                        )?.label
                      }
                    </span>
                    <span
                      className="badge importance-badge"
                      style={{
                        backgroundColor: getImportanceColor(memory.importance),
                      }}
                    >
                      {
                        memoryService.MEMORY_IMPORTANCE.find(
                          i => i.value === memory.importance
                        )?.label
                      }
                    </span>
                  </div>
                  <div className="memory-actions">
                    {statusFilter === 'active' && (
                      <>
                        <button
                          className="action-btn edit"
                          onClick={() => handleOpenEditModal(memory)}
                          title="Modifica"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="action-btn archive"
                          onClick={() => handleArchiveMemory(memory.id)}
                          title="Archivia"
                        >
                          <FiArchive />
                        </button>
                      </>
                    )}
                    <button
                      className="action-btn delete"
                      onClick={() => handleDeleteMemory(memory.id)}
                      title="Elimina"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
                <div className="memory-content-text">{memory.content}</div>
                {memory.tags && memory.tags.length > 0 && (
                  <div className="memory-tags">
                    {memory.tags.map((tag, idx) => (
                      <span key={idx} className="tag">
                        <FiTag size={12} /> {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="memory-card-footer">
                  <span className="memory-date">
                    {new Date(memory.created_at).toLocaleDateString('it-IT', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="page-button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              <FiChevronLeft /> Precedente
            </button>
            <span className="page-info">
              Pagina {currentPage} di {totalPages}
            </span>
            <button
              className="page-button"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Successiva <FiChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'create' ? 'Nuova Memoria' : 'Modifica Memoria'}</h2>
              <button className="close-button" onClick={handleCloseModal}>
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              {/* Content */}
              <div className="form-group">
                <label>
                  Contenuto <span className="required">*</span>
                </label>
                <textarea
                  value={formData.content}
                  onChange={e => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Descrivi cosa F.R.A.N.C.A. dovrebbe ricordare..."
                  rows={6}
                  maxLength={MAX_CONTENT_LENGTH}
                />
                <span
                  className={
                    formData.content.length > MAX_CONTENT_LENGTH * 0.9
                      ? 'char-count warning'
                      : 'char-count'
                  }
                >
                  {formData.content.length}/{MAX_CONTENT_LENGTH}
                </span>
              </div>

              {/* Category */}
              <div className="form-group">
                <label>Categoria</label>
                <select
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                >
                  {memoryService.MEMORY_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Importance */}
              <div className="form-group">
                <label>Importanza</label>
                <select
                  value={formData.importance}
                  onChange={e =>
                    setFormData({ ...formData, importance: e.target.value })
                  }
                >
                  {memoryService.MEMORY_IMPORTANCE.map(imp => (
                    <option key={imp.value} value={imp.value}>
                      {imp.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div className="form-group">
                <label>Tag (opzionali)</label>
                <div className="tag-input-wrapper">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddTag()
                      }
                    }}
                    placeholder="Aggiungi un tag..."
                  />
                  <button
                    type="button"
                    className="add-tag-button"
                    onClick={handleAddTag}
                  >
                    <FiPlus />
                  </button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="tags-list">
                    {formData.tags.map((tag, idx) => (
                      <span key={idx} className="tag editable">
                        <FiTag size={12} /> {tag}
                        <button onClick={() => handleRemoveTag(tag)}>
                          <FiX size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={handleCloseModal}
                disabled={isSaving}
              >
                Annulla
              </button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  textTransform: 'none',
                  backgroundColor: 'primary.main',
                  px: 4,
                  borderRadius: 2,
                  gap: '8px',
                }}
                onClick={handleSaveMemory}
                disabled={isSaving || !formData.content.trim()}
              >
                {isSaving ? (
                  <>
                    <FiLoader className="spinner" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <FiSave />
                    Salva
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MemoryManagementPage
